import {
    attachClosestEdge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { zodResolver } from "@hookform/resolvers/zod";
import { GripVertical, HelpCircle, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Control, SubmitHandler, useFieldArray, useForm } from "react-hook-form";

import {
    FormTrialGames,
    TournamentStageFormInput,
    zFormEliminationFormat,
    zFormGamePointSystem,
    zFormTrialPointSystem,
    zTournamentStageFormInput,
} from "@/components/forms/validation/tournament-stage";
import EditorInput from "@/components/inputs/EditorInput";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { TieBreaker } from "@/gen/realtime";
import { cx } from "@/utils/cx";

export const TournamentStageForm = ({
    onSubmit,
    defaultValues,
}: {
    onSubmit: SubmitHandler<TournamentStageFormInput>;
    defaultValues: TournamentStageFormInput;
}) => {
    const form = useForm<TournamentStageFormInput>({
        defaultValues: defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zTournamentStageFormInput),
    });
    const trialFormat = form.watch("trialFormat");
    const tieBreakers = form.watch("tieBreakers");
    const eliminationSettings = form.watch("eliminationSettings");
    const kind = form.watch("kind");

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input placeholder="Title" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="order"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Order number</FormLabel>
                            <FormControl>
                                <Input placeholder="Order number" type={"number"} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormItem>
                    <FormLabel>Trial format</FormLabel>
                    <div>
                        {trialFormat === null ? (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => form.setValue("trialFormat", { steps: [{}] })}
                            >
                                Specify trial format
                            </Button>
                        ) : (
                            <TrialFormatSubform
                                control={form.control}
                                onClear={() => form.setValue("trialFormat", null)}
                            />
                        )}
                    </div>
                </FormItem>
                <FormItem>
                    <FormLabel>Tie breakers</FormLabel>
                    <div>
                        {tieBreakers === null ? (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => form.setValue("tieBreakers", [])}
                            >
                                Specify tie breakers format
                            </Button>
                        ) : (
                            <TieBreakersSubform
                                control={form.control}
                                onClear={() => form.setValue("tieBreakers", null)}
                            />
                        )}
                    </div>
                </FormItem>
                <FormField
                    control={form.control}
                    name="kind"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Kind</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a stage kind" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="POOL">Pool</SelectItem>
                                    <SelectItem value="ELIMINATION">Elimination</SelectItem>
                                    <SelectItem value="UNSUPPORTED">Unsupported</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {kind === "ELIMINATION" && (
                    <FormItem>
                        <FormLabel>Elimination Settings</FormLabel>
                        <div>
                            {eliminationSettings === null ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() =>
                                        form.setValue("eliminationSettings", {
                                            format: null,
                                            include_third_place_match: false,
                                            initial_pairings: [],
                                        })
                                    }
                                >
                                    Specify elimination settings
                                </Button>
                            ) : (
                                <EliminationSettingsSubform
                                    control={form.control}
                                    onClear={() => form.setValue("eliminationSettings", null)}
                                />
                            )}
                        </div>
                        <FormMessage />
                    </FormItem>
                )}
                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <EditorInput
                                    initialValue={field.value}
                                    onChange={value => form.setValue("description", value)}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button
                    type="submit"
                    disabled={!form.formState.isValid || form.formState.isSubmitting}
                >
                    Save
                </Button>
            </form>
        </Form>
    );
};

function TieBreakerSubformOptions({
    control,
    tb,
    idx,
    className,
}: {
    control: Control<TournamentStageFormInput>;
    tb: TieBreaker;
    idx: number;
    className?: string;
}) {
    switch (tb.t) {
        case "Buchholz":
            return (
                <FormField
                    control={control}
                    name={`tieBreakers.${idx}.c.cut`}
                    render={({ field }) => (
                        <div className={cx(className, "flex flex-row items-center gap-2")}>
                            <FormLabel className="flex-1 text-right">Cut:</FormLabel>
                            <Input
                                className="flex-1"
                                type="number"
                                {...field}
                                min={0}
                                value={field.value ?? undefined}
                                onChange={e => {
                                    const value = e.target.valueAsNumber;
                                    field.onChange(value);
                                }}
                            />
                        </div>
                    )}
                />
            );
        case "AverageOpponentRating": {
            return (
                <FormField
                    control={control}
                    name={`tieBreakers.${idx}.c.cut`}
                    render={({ field }) => (
                        <div className={cx(className, "flex flex-row items-center gap-2")}>
                            <FormLabel className="flex-1 text-right">Cut:</FormLabel>
                            <Input
                                className="flex-1"
                                type="number"
                                {...field}
                                value={field.value ?? undefined}
                                onChange={e => {
                                    const value = e.target.valueAsNumber;
                                    field.onChange(value);
                                }}
                            />
                        </div>
                    )}
                />
            );
        }
        case "DirectEncounter": {
            return (
                <FormField
                    control={control}
                    name={`tieBreakers.${idx}.c.onlyTwoWay`}
                    render={({ field }) => (
                        <div className={cx(className, "flex flex-row items-center gap-2")}>
                            <FormLabel className="flex-1 text-right">Only two way:</FormLabel>
                            <div className="w100 flex-1">
                                <Select
                                    onValueChange={val =>
                                        field.onChange(val === "true" ? true : false)
                                    }
                                    value={field.value ? "true" : "false"}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Only two way" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Yes</SelectItem>
                                        <SelectItem value="false">No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                />
            );
        }
        case "NumberOfWins":
        case "GamesPlayedWithBlack":
        case "SonnebornBerger":
        default:
            return null;
    }
}

type TieBreakersSubformProps = {
    control: Control<TournamentStageFormInput>;
    onClear: () => void;
};

type TieBreakerTypes = TieBreaker["t"];

function TieBreakersSubform({ control, onClear }: TieBreakersSubformProps) {
    const tieBreakers = useFieldArray({
        control: control,
        name: "tieBreakers",
    });

    const [selected, setSelected] = useState<TieBreakerTypes>("Buchholz" as const);

    const onAddTieBreaker = useCallback(() => {
        switch (selected) {
            case "Buchholz":
                tieBreakers.append({ t: "Buchholz", c: { cut: 1 } });
                break;
            case "DirectEncounter":
                tieBreakers.append({ t: "DirectEncounter", c: { onlyTwoWay: false } });
                break;
            case "AverageOpponentRating":
                tieBreakers.append({ t: "AverageOpponentRating", c: { cut: null } });
                break;
            case "NumberOfWins":
                tieBreakers.append({ t: "NumberOfWins", c: {} });
                break;
            case "GamesPlayedWithBlack":
                tieBreakers.append({ t: "GamesPlayedWithBlack", c: {} });
                break;
            case "NumberOfWinsWithBlack":
                tieBreakers.append({ t: "NumberOfWinsWithBlack", c: {} });
                break;
            case "SonnebornBerger":
                tieBreakers.append({ t: "SonnebornBerger", c: {} });
                break;
        }
    }, [selected, tieBreakers]);

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2">
                {tieBreakers.fields.map((tb, idx) => (
                    <div key={idx} className="flex h-[50px] flex-row items-center gap-2">
                        <FormLabel className="flex-1">
                            {`${idx + 1}. `}
                            {tb.t}
                        </FormLabel>
                        <TieBreakerSubformOptions
                            className="flex-1"
                            control={control}
                            tb={tb}
                            idx={idx}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            className="px-2"
                            onClick={() => tieBreakers.remove(idx)}
                        >
                            <X />
                        </Button>
                    </div>
                ))}
            </div>
            <div className="flex flex-row items-center gap-2">
                <div className="flex-1">
                    <Select
                        onValueChange={val => setSelected(val as TieBreakerTypes)}
                        value={selected}
                    >
                        <FormControl>
                            <SelectTrigger>
                                <SelectValue placeholder="Select a stage kind" />
                            </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                            <SelectItem value="Buchholz">Buchholz</SelectItem>
                            <SelectItem value="GamesPlayedWithBlack">
                                Games played with black
                            </SelectItem>
                            <SelectItem value="NumberOfWinsWithBlack">
                                Number of wins with black
                            </SelectItem>
                            <SelectItem value="DirectEncounter">Direct Encounter</SelectItem>
                            <SelectItem value="AverageOpponentRating">
                                Average Opponent Rating
                            </SelectItem>
                            <SelectItem value="NumberOfWins">Number of wins</SelectItem>
                            <SelectItem value="SonnebornBerger">Sonneborn Berger</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex-2">
                    <Button type="button" variant="ghost" onClick={() => onAddTieBreaker()}>
                        Add tie breaker
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => {
                            if (confirm("Clear all steps?")) {
                                onClear();
                            }
                        }}
                    >
                        Clear
                    </Button>
                </div>
            </div>
        </div>
    );
}

type TrialFormatSubformProps = {
    control: Control<TournamentStageFormInput>;
    onClear: () => void;
};

function TrialFormatSubform({ control, onClear }: TrialFormatSubformProps) {
    const trialFormatSteps = useFieldArray({
        control: control,
        name: "trialFormat.steps",
    });

    const pointSystemExplanation: Record<string, string> = {
        Standard: "1 point for win, 0.5 for draw.",
        Double: "2 points for win, 1 for draw.",
        SumPoints: "Sum of points across all games in trial.",
    };

    return (
        <div className="flex flex-col gap-8">
            <FormField
                control={control}
                name="trialFormat.points"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Point system</FormLabel>
                        <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value ?? undefined}
                        >
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select point system" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {zFormTrialPointSystem.options.map(option => (
                                    <SelectItem key={option} value={option}>
                                        {option} - {pointSystemExplanation[option]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="flex flex-col gap-2">
                {trialFormatSteps.fields.map((step, index) => (
                    <div key={step.id} className="flex flex-col gap-2 rounded border p-3">
                        <div className="flex flex-row items-center gap-2">
                            <FormLabel htmlFor={step.id} className="w-[60px]">
                                Step&nbsp;{index + 1}
                            </FormLabel>

                            <FormField
                                control={control}
                                name={`trialFormat.steps.${index}.point_system`}
                                render={({ field }) => {
                                    return (
                                        <Select
                                            onValueChange={field.onChange}
                                            defaultValue={field.value ?? undefined}
                                        >
                                            <FormControl className="flex-1">
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Point system" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {zFormGamePointSystem.options.map(option => (
                                                    <SelectItem key={option} value={option}>
                                                        {option}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    );
                                }}
                            />

                            <FormField
                                control={control}
                                name={`trialFormat.steps.${index}.games`}
                                render={({ field }) => (
                                    <GamesInStepInput
                                        value={field.value ?? undefined}
                                        onChange={field.onChange}
                                    />
                                )}
                            />

                            <Button
                                type="button"
                                variant="ghost"
                                className="px-2"
                                onClick={() => trialFormatSteps.remove(index)}
                            >
                                <X />
                            </Button>
                        </div>

                        <FormField
                            control={control}
                            name={`trialFormat.steps.${index}.time_control`}
                            render={({ field }) => (
                                <FormItem>
                                    <div className="flex flex-row items-center gap-2">
                                        <FormLabel className="w-[60px] text-sm">Time</FormLabel>
                                        <FormControl className="flex-1">
                                            <Input
                                                placeholder="10+5"
                                                {...field}
                                                value={field.value ?? ""}
                                            />
                                        </FormControl>
                                        <div className="min-w-[200px] text-xs text-gray-500">
                                            Format: minutes+increment (seconds)
                                        </div>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                ))}
            </div>
            <div className="flex flex-row items-center gap-2">
                <Button type="button" variant="ghost" onClick={() => trialFormatSteps.append({})}>
                    Add step
                </Button>
                <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                        if (confirm("Clear all steps?")) {
                            onClear();
                        }
                    }}
                >
                    Clear
                </Button>
            </div>
        </div>
    );
}

type GamesInStepProps = {
    value: FormTrialGames | undefined;
    onChange: (value: FormTrialGames) => void;
};

function GamesInStepInput({ value, onChange }: GamesInStepProps) {
    const initialVariant =
        value !== undefined ? ("Fixed" in value ? "Fixed" : "RepeatUntilDecisive") : undefined;
    const initialNumGames =
        value !== undefined ? ("Fixed" in value ? value.Fixed : value.RepeatUntilDecisive) : 1;

    const [variant, setVariant] = useState<"Fixed" | "RepeatUntilDecisive" | undefined>(
        initialVariant
    );
    const [numGames, setNumGames] = useState<number>(initialNumGames);

    const handleVariantChange = useCallback(
        (newVariant: "Fixed" | "RepeatUntilDecisive") => {
            setVariant(newVariant);
            onChange(
                newVariant === "Fixed" ? { Fixed: numGames } : { RepeatUntilDecisive: numGames }
            );
        },
        [numGames, onChange]
    );

    const handleNumGamesChange = useCallback(
        (newNumGames: number) => {
            setNumGames(newNumGames);
            if (variant === null) {
                return;
            }

            onChange(
                variant === "Fixed" ? { Fixed: newNumGames } : { RepeatUntilDecisive: newNumGames }
            );
        },
        [variant, onChange]
    );

    return (
        <>
            <Select onValueChange={handleVariantChange} defaultValue={variant}>
                <FormControl className="flex-1">
                    <SelectTrigger>
                        <SelectValue placeholder="Game mapping" />
                    </SelectTrigger>
                </FormControl>
                <SelectContent>
                    <SelectItem value="Fixed">Predetermined number of games</SelectItem>
                    <SelectItem value="RepeatUntilDecisive">
                        Step repeats until decisive result
                    </SelectItem>
                </SelectContent>
            </Select>

            <Input
                className="flex-1"
                type="number"
                value={numGames}
                onChange={e => handleNumGamesChange(e.target.valueAsNumber)}
            />
        </>
    );
}

type EliminationSettingsSubformProps = {
    control: Control<TournamentStageFormInput>;
    onClear: () => void;
};

function EliminationSettingsSubform({ control, onClear }: EliminationSettingsSubformProps) {
    const pairings = useFieldArray({
        control: control,
        name: "eliminationSettings.initial_pairings",
    });

    const movePairing = useCallback(
        (fromIndex: number, toIndex: number) => {
            pairings.move(fromIndex, toIndex);
        },
        [pairings]
    );

    return (
        <div className="flex flex-col gap-8">
            <FormField
                control={control}
                name="eliminationSettings.format"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Elimination Format</FormLabel>
                        <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value ?? undefined}
                        >
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select elimination format" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {zFormEliminationFormat.options.map(option => (
                                    <SelectItem
                                        key={option}
                                        value={option}
                                        disabled={option === "double"}
                                    >
                                        {option === "single"
                                            ? "Single Elimination"
                                            : "Double Elimination (not supported yet)"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="eliminationSettings.include_third_place_match"
                render={({ field }) => (
                    <div className="flex flex-row items-center gap-2">
                        <FormLabel className="flex-1">Include third place match:</FormLabel>
                        <div className="flex-1">
                            <Select
                                onValueChange={val => field.onChange(val === "true")}
                                value={field.value ? "true" : "false"}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="true">Yes</SelectItem>
                                    <SelectItem value="false">No</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}
            />

            <FormField
                control={control}
                name="eliminationSettings.initial_pairings"
                render={() => (
                    <FormItem>
                        <div className="mb-2 flex items-center gap-2">
                            <FormLabel>Initial Pairings</FormLabel>
                            <BracketHelp />
                        </div>
                        <div className="flex flex-col gap-2">
                            {pairings.fields.map((pairing, index) => (
                                <DraggablePairingRow
                                    key={pairing.id}
                                    index={index}
                                    control={control}
                                    onRemove={() => pairings.remove(index)}
                                    onMove={movePairing}
                                />
                            ))}
                        </div>
                        <div className="mt-2 flex flex-row gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() =>
                                    pairings.append({ player1_id: null, player2_id: null })
                                }
                            >
                                Add pairing
                            </Button>
                        </div>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <Button
                type="button"
                variant="destructive"
                onClick={() => {
                    if (confirm("Clear elimination settings?")) {
                        onClear();
                    }
                }}
            >
                Clear
            </Button>
        </div>
    );
}

type DraggablePairingRowProps = {
    index: number;
    control: Control<TournamentStageFormInput>;
    onRemove: () => void;
    onMove: (fromIndex: number, toIndex: number) => void;
};

function DraggablePairingRow({ index, control, onRemove, onMove }: DraggablePairingRowProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const [closestEdge, setClosestEdge] = useState<"top" | "bottom" | null>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const draggableCleanup = draggable({
            element,
            getInitialData: () => ({ index, type: "pairing" }),
            onDragStart: () => setIsDragging(true),
            onDrop: () => setIsDragging(false),
        });

        const dropTargetCleanup = dropTargetForElements({
            element,
            getData: ({ input }) => {
                return attachClosestEdge(
                    { index, type: "pairing" },
                    {
                        element,
                        input,
                        allowedEdges: ["top", "bottom"],
                    }
                );
            },
            onDragEnter: ({ self }) => {
                setIsDraggedOver(true);
                const edge = extractClosestEdge(self.data);
                if (edge === "top" || edge === "bottom") {
                    setClosestEdge(edge);
                }
            },
            onDragLeave: () => {
                setIsDraggedOver(false);
                setClosestEdge(null);
            },
            onDrop: ({ self, source }) => {
                setIsDraggedOver(false);
                setClosestEdge(null);

                const sourceData = source.data as { index: number; type: string };
                const targetData = self.data as { index: number; type: string };

                if (sourceData.type !== "pairing" || targetData.type !== "pairing") {
                    return;
                }

                const edge = extractClosestEdge(self.data);
                const newIndex = getReorderDestinationIndex({
                    startIndex: sourceData.index,
                    closestEdgeOfTarget: edge,
                    indexOfTarget: targetData.index,
                    axis: "vertical",
                });

                if (newIndex !== sourceData.index) {
                    onMove(sourceData.index, newIndex);
                }
            },
        });

        return () => {
            draggableCleanup();
            dropTargetCleanup();
        };
    }, [index, onMove]);

    return (
        <div
            ref={ref}
            className={cx(
                "flex flex-row items-center gap-2 rounded border p-2 transition-all",
                isDragging && "opacity-50",
                isDraggedOver && closestEdge === "top" && "border-t-2 border-t-blue-500",
                isDraggedOver && closestEdge === "bottom" && "border-b-2 border-b-blue-500",
                !isDragging && "cursor-grab hover:bg-gray-50 active:cursor-grabbing"
            )}
        >
            <div className="flex cursor-grab items-center">
                <GripVertical className="h-4 w-4 text-gray-400" />
            </div>

            <FormLabel className="w-[80px]">Pair {index + 1}:</FormLabel>

            <FormField
                control={control}
                name={`eliminationSettings.initial_pairings.${index}.player1_id`}
                render={({ field }) => (
                    <PlayerIdInput
                        className="max-w-[500px] flex-1"
                        playerId={field.value || null}
                        onPlayerIdChange={value => field.onChange(value || null)}
                    />
                )}
            />

            <span>vs</span>

            <FormField
                control={control}
                name={`eliminationSettings.initial_pairings.${index}.player2_id`}
                render={({ field }) => (
                    <PlayerIdInput
                        className="max-w-[500px] flex-1"
                        playerId={field.value || null}
                        onPlayerIdChange={value => field.onChange(value || null)}
                    />
                )}
            />

            <Button type="button" variant="ghost" className="px-2" onClick={onRemove}>
                <X />
            </Button>
        </div>
    );
}

function BracketHelp() {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Help for bracket pairing order"
                >
                    <HelpCircle className="h-4 w-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>How pairing order affects the bracket</DialogTitle>
                    <DialogDescription>
                        The order of initial pairings determines the bracket structure and which
                        players can meet in later rounds. Make sure to add the pairings in the exact
                        order they should appear in the tournament bracket.
                    </DialogDescription>
                </DialogHeader>
                <ExampleBracketTree />
            </DialogContent>
        </Dialog>
    );
}

function ExampleBracketTree() {
    return (
        <div className="py-4">
            <div className="mb-3 text-sm font-medium text-gray-700">
                Example: 8-player single elimination bracket
            </div>
            <div className="relative">
                {/* First round */}
                <div className="grid grid-cols-7 items-center gap-4">
                    {/* Round 1 matches */}
                    <div className="col-span-2 space-y-8">
                        <div className="space-y-2">
                            <div className="rounded border bg-white px-3 py-2">
                                <div className="font-medium">Pair 1</div>
                                <div className="text-xs text-gray-500">Player A vs Player B</div>
                            </div>
                            <div className="rounded border bg-white px-3 py-2">
                                <div className="font-medium">Pair 2</div>
                                <div className="text-xs text-gray-500">Player C vs Player D</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="rounded border bg-white px-3 py-2">
                                <div className="font-medium">Pair 3</div>
                                <div className="text-xs text-gray-500">Player E vs Player F</div>
                            </div>
                            <div className="rounded border bg-white px-3 py-2">
                                <div className="font-medium">Pair 4</div>
                                <div className="text-xs text-gray-500">Player G vs Player H</div>
                            </div>
                        </div>
                    </div>

                    <div className="relative col-span-1 h-full">
                        <svg
                            className="absolute inset-0 h-full w-full"
                            viewBox="0 0 40 200"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M 0,20 L 20,20 L 20,50 L 40,50"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                            <path
                                d="M 0,60 L 20,60 L 20,50 L 40,50"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                            <path
                                d="M 0,140 L 20,140 L 20,150 L 40,150"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                            <path
                                d="M 0,180 L 20,180 L 20,150 L 40,150"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                        </svg>
                    </div>

                    {/* Semifinals */}
                    <div className="col-span-2 flex flex-col justify-center space-y-8">
                        <div className="rounded border bg-gray-50 px-3 py-4">
                            <div className="text-center font-medium">Semifinal 1</div>
                            <div className="mt-1 text-center text-xs text-gray-500">
                                Winner P1 vs Winner P2
                            </div>
                        </div>

                        <div className="rounded border bg-gray-50 px-3 py-4">
                            <div className="text-center font-medium">Semifinal 2</div>
                            <div className="mt-1 text-center text-xs text-gray-500">
                                Winner P3 vs Winner P4
                            </div>
                        </div>
                    </div>

                    <div className="relative col-span-1 h-full">
                        <svg
                            className="absolute inset-0 h-full w-full"
                            viewBox="0 0 40 200"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M 0,50 L 20,50 L 20,100 L 40,100"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                            <path
                                d="M 0,150 L 20,150 L 20,100 L 40,100"
                                stroke="#94a3b8"
                                strokeWidth="1"
                                fill="none"
                            />
                        </svg>
                    </div>

                    <div className="col-span-1 flex items-center">
                        <div className="w-full rounded border-2 border-yellow-400 bg-yellow-50 px-3 py-4">
                            <div className="text-center font-bold">Final</div>
                            <div className="mt-1 text-center text-xs text-gray-600">
                                Winner SF1 vs Winner SF2
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
